<template>
	<el-popover
		ref="popover"
		v-model="showDropdown"
		v-click-outside="onClickOutside"
		placement="bottom"
		width="250"
		trigger="manual"
		popper-class="p-0"
	>
		<base-button
			@click="showDropdown = !showDropdown"
			slot="reference"
			class="bg-transparent text-dark px-3 border-light shadow-none text-left"
			block
			>Select Company</base-button
		>
		<validation-provider
			:rules="rules"
			:name="name"
			v-bind="$attrs"
			v-slot="{ errors }"
		>
			<b-form-group label-class="m-0 d-flex align-items-center">
				<template #label>
					<b-icon
						v-if="isLoading"
						icon="circle-fill"
						animation="fade"
						font-scale="0.5"
						class="mt-1 ml-2"
					></b-icon>
				</template>
				<input type="hidden" v-model="innerValue" />
				<div key="tab-list">
					<div class="p-3">
						<b-form-input
							@input="onSearch"
							:placeholder="`Search ${label}`"
							size="sm"
						></b-form-input>
					</div>

					<a
						v-if="multiple"
						@click.prevent="selectAll"
						class="mt-2 d-block small"
						href
						>{{
							innerValue && innerValue.length
								? "Unselect"
								: "Select"
						}}
						All</a
					>

					<b-list-group v-if="0 < dropdownOptions.length">
						<b-list-group-item
							v-for="(val, key) in dropdownOptions"
							@click.stop="onSelect(val.value)"
							:key="`option-${key}`"
							:active="isSelected(val.value)"
							active-class="bg-light"
							class="py-2 px-3 border-right-0 border-left-0 border-bottom-0 rounded-0"
							button
							>{{ val.text }}</b-list-group-item
						>
					</b-list-group>
					<small v-else class="d-block py-2 px-3"
						>No {{ label }} found.</small
					>
				</div>
				<slot name="error">
					<div
						v-if="errors[0]"
						class="invalid-feedback"
						style="display: block"
					>
						{{ errors[0] }}
					</div>
				</slot>
			</b-form-group>
		</validation-provider>
	</el-popover>
</template>

<script>
import Fuse from "fuse.js";

export default {
	name: "searchable-dropdown",
	props: {
		disabled: Boolean,
		isLoading: Boolean,
		multiple: Boolean,
		label: {
			type: String,
			description: "Input label",
		},
		name: {
			type: String,
			description: "Input name",
		},
		options: {
			type: Array,
			description: "Option dropdown items",
			required: true,
		},
		rules: {
			type: [String, Array],
			description: "Validation rules",
		},
		value: {
			type: [String, Number, Array],
			description: "Input value",
		},
		dropdownPosition: {
			type: String,
			description: "Dropdown options position. (top|right|bottom|left)",
			validator: function (value) {
				return 0 <= ["top", "right", "bottom", "left"].indexOf(value);
			},
		},
	},
	data() {
		return {
			showDropdown: false,
			searchQuery: undefined,
			innerValue: undefined,
		};
	},
	computed: {
		dropdownOptions() {
			let options = this.options;
			const fuse = new Fuse(options, {
				keys: ["text", "value"],
				includeScore: true,
				useExtendedSearch: true,
				threshold: 0.3,
			});

			if (this.searchQuery)
				options = fuse
					.search(`'${this.searchQuery}`)
					.map((item) => item.item);

			return _.orderBy(options, "text", "asc");
		},
		selectedOption() {
			if (!this.dropdownOptions.length || !this.innerValue)
				return `Select ${this.label}`;

			if (this.multiple) {
				return this.innerValue
					? this.innerValue.join(",")
					: `Select ${this.label}`;
			} else {
				const idx = this.dropdownOptions.findIndex(
					(item) => item.value == this.innerValue
				);

				return 0 <= idx
					? this.dropdownOptions[idx].text
					: `Select ${this.label}`;
			}
		},
	},
	watch: {
		value(newVal) {
			if (!newVal) this.innerValue = newVal;
		},
		innerValue(newVal) {
			this.$emit("input", newVal);
		},
		dropdownOptions() {
			this.innerValue = undefined;
		},
	},
	methods: {
		onClickOutside() {
			if (this.showDropdown) this.showDropdown = false;
		},
		isSelected(value) {
			if (this.multiple) {
				return this.innerValue
					? 0 <= this.innerValue.indexOf(value)
					: false;
			} else {
				return this.innerValue == value;
			}
		},
		onSearch: _.debounce(function (keyword) {
			this.searchQuery = keyword;
		}, 500),
		onSelect(value) {
			if (this.multiple) {
				this.innerValue = this.innerValue || [];

				const idx = this.innerValue.indexOf(value);

				if (0 <= idx) this.innerValue.splice(idx, 1);
				else this.innerValue.push(value);
			} else {
				this.showDropdown = !this.showDropdown;
				this.innerValue = value;
			}

			this.$emit("select", this.innerValue);
		},
		selectAll() {
			if (!this.multiple) return false;

			this.innerValue = this.innerValue
				? undefined
				: this.options.map((item) => item.value);
		},
	},
	created() {
		if (this.value) this.onSelect(this.value);
	},
};
</script>

<style lang="scss" scoped>
.list-group {
	max-height: 200px;
	overflow-y: auto;
}
</style>
